import { useEffect } from "react"
import { observer } from "mobx-react-lite"
import { Link, useLocation } from "react-router-dom"

import type { TokenName } from "@/domains/core"
import type { Sort } from "@/domains/table-processor"
import { Button } from "@/components/button"
import { ShowWhenTrue } from "@/components/conditionals"
import { Search } from "@/components/search"
import { TooltipProvider } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { SwitchDetailedMode } from "@/domains/common/components/switches/switch-detailed-mode"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { CL_PRODUCT_COPY } from "@/domains/manual-cl-page/cl-product-copy"
import { manualClProcessor } from "@/domains/manual-cl-page/manual-cl-processor"
import { MANUAL_CL_V1_BREADCRUMBS } from "@/domains/thick-v1/breadcrumbs"
import { IndexerFreshnessBanner } from "@/domains/thick-v1/components/indexer-freshness-banner"
import { ManualClTable } from "@/domains/thick-v1/components/manual-cl-table/manual-cl-table"
import { ManualClViewTabs } from "@/domains/thick-v1/components/manual-cl-view-tabs"
import { ManualClMyPositionsTable } from "@/domains/thick-v1/components/my-positions/manual-cl-my-positions-table"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useGovernanceToken } from "@/hooks/use-governance-token"
import { useNativeToken } from "@/hooks/use-native-token"

const ManualCLIndexPageContent = observer(() => {
  const scaleApp = useScaleApplication()
  const location = useLocation()
  const nativeToken = useNativeToken()
  const governanceToken = useGovernanceToken()
  const chainConfig = useChainConfig()

  const currentPageNumber = scaleApp.Pages.ManualClV1.currentPageNumber
  const setCurrentPageNumber = (value: number) => scaleApp.Pages.ManualClV1.setCurrentPageNumberFromUi(value)
  const pageSize = scaleApp.Pages.ManualClV1.pageSize
  const setPageSize = (value: number) => scaleApp.Pages.ManualClV1.setPageSizeFromUi(value)
  const sortBy = scaleApp.Pages.ManualClV1.sortBy
  const setSortBy = (value: Sort) => scaleApp.Pages.ManualClV1.setSortByFromUi(value)
  const isCompact = !scaleApp.Pages.ManualClV1.isDetailed
  const search = scaleApp.Pages.ManualClV1.search
  const setSearch = (value: string) => scaleApp.Pages.ManualClV1.setSearchFromUi(value)
  const view = scaleApp.Pages.ManualClV1.view
  const setView = (value: typeof view) => scaleApp.Pages.ManualClV1.setViewFromUi(value)
  const isDetailed = scaleApp.Pages.ManualClV1.isDetailed
  const setIsDetailed = (value: boolean) => scaleApp.Pages.ManualClV1.setIsDetailedFromUi(value)

  // Get pools from repository and process them
  const manualClPools = scaleApp.ManualClV1.Repository.getAll()
  const processed = manualClProcessor(manualClPools, search, sortBy)

  const title = CL_PRODUCT_COPY.v1.liquidityTitle

  useEffect(() => {
    const viewParam = new URLSearchParams(location.search).get("view")
    if (viewParam !== "all-pools" && viewParam !== "my-positions") return
    if (viewParam === scaleApp.Pages.ManualClV1.view) return
    scaleApp.Pages.ManualClV1.setViewFromUi(viewParam)
  }, [location.search, scaleApp])

  return (
    <>
      <MetaData title={title} />
      <PageTitle title={title} items={MANUAL_CL_V1_BREADCRUMBS} />
      <TooltipProvider>
        <IndexerFreshnessBanner className="mb-4" />
        <div className="flex w-full flex-col gap-6 rounded-lg border border-p300-light bg-p300 p-4 text-t100 lg:p-6">
          {/* Title section */}
          <div className="flex w-full flex-col items-center justify-between gap-2 md:flex-row">
            <div className="flex w-full flex-grow flex-col gap-1">
              <h1 className="text-md font-medium">{CL_PRODUCT_COPY.v1.shortName}</h1>
              <p className="text-sm text-t200">Add positions to pools and earn {governanceToken.symbol}</p>
            </div>
            <div className="flex w-full min-w-fit items-center justify-between gap-6 md:w-fit">
              <SwitchDetailedMode checked={isDetailed} onCheckedChange={setIsDetailed} />
              <Button variant={"primary"} className="min-w-fit text-nowrap" asChild>
                <Link to={"/liquidity/cl/v1/create"}>Create New Pool</Link>
              </Button>
            </div>
          </div>

          {/* We currently reuse the params for both views - split it later if it's an issue*/}
          <ManualClTableParams
            governanceToken={governanceToken}
            nativeToken={nativeToken}
            search={search}
            onSearchChange={setSearch}
            view={view}
            onViewChange={setView}
          />

          <ShowWhenTrue when={view === "all-pools"}>
            <ManualClTable
              chainConfig={chainConfig}
              pools={processed.rows}
              currentPageNumber={currentPageNumber}
              setCurrentPageNumber={setCurrentPageNumber}
              pageSize={pageSize}
              setPageSize={setPageSize}
              sortBy={sortBy}
              setSortBy={setSortBy}
              isCompact={isCompact}
            />
          </ShowWhenTrue>

          <ShowWhenTrue when={view === "my-positions"}>
            <ManualClMyPositionsTable
              currentPageNumber={currentPageNumber}
              setCurrentPageNumber={setCurrentPageNumber}
              pageSize={pageSize}
              setPageSize={setPageSize}
              sortBy={sortBy}
              setSortBy={setSortBy}
              isCompact={isCompact}
              search={search}
            />
          </ShowWhenTrue>
        </div>
      </TooltipProvider>
    </>
  )
})

const ManualCLIndexPage = observer(() => {
  const scaleApp = useScaleApplication()

  return (
    <PageContainer>
      <ManualCLIndexPageContent />
    </PageContainer>
  )
})

export default ManualCLIndexPage

function ManualClTableParams({
  nativeToken,
  governanceToken,
  search,
  onSearchChange,
  view,
  onViewChange,
}: {
  nativeToken: TokenName
  governanceToken: TokenName
  search: string
  onSearchChange: (value: string) => void
  view: "all-pools" | "my-positions"
  onViewChange: (value: "all-pools" | "my-positions") => void
}) {
  return (
    <>
      {/* mobile / tablet view */}
      <div className="flex w-full justify-between gap-4 lg:hidden">
        <div className="">
          <Search
            className="w-full text-t200"
            placeholder={`${governanceToken.name}, ${nativeToken.name}, BTC, ETH, 0x...`}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
        <ManualClViewTabs value={view} onValueChange={onViewChange} className="bg-p400" />
      </div>

      {/* desktop view */}
      <div className="hidden w-full justify-between gap-4 lg:flex">
        <div className="flex min-w-[280px] xl:min-w-[440px]">
          <Search
            className="w-full text-t200"
            placeholder={`${governanceToken.name}, ${nativeToken.name}, BTC, ETH, 0x...`}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
        <ManualClViewTabs value={view} onValueChange={onViewChange} className="bg-p400" />
      </div>
    </>
  )
}
